/**
 * Tavern FX events — transient UI feedback for tavern-phase state changes.
 *
 * The store computes these by diffing state before/after an action
 * (battlecries, on-buy hooks, hero passives — anything that mutates
 * stats), pushes them onto `tavernFx`, and TavernPage plays them as slot
 * flashes / floating "+X/+Y" text. Diffing (rather than threading event
 * emission through every ability path) means card triggers AND the
 * mutate-in-place v1 hero-power helpers are covered by one mechanism.
 *
 * Events are UI-only: never persisted, never read by game logic.
 */

import type { Card } from './data/cards/types';

export type TavernFxEvent =
  | {
      id: number;
      kind: 'board_buff';
      slot: number;
      atk: number;
      hp: number;
      /** True for "this combat only" (combatBuff) changes. */
      temp: boolean;
    }
  | { id: number; kind: 'shop_buff'; instanceId: string; atk: number; hp: number }
  | { id: number; kind: 'hero_heal'; amount: number }
  | { id: number; kind: 'gold_gain'; amount: number };

// Monotonic id so the UI can track which events it has already played.
// UI-only, so module-level mutable state is fine.
let fxIdCounter = 1;
export function nextFxId(): number {
  return fxIdCounter++;
}

export interface TavernFxSnapshot {
  board: (Card | null)[];
  shop: Card[];
  health: number;
}

/**
 * Diff two tavern snapshots into FX events.
 *
 * - Board cards are matched per-slot by instanceId; stat deltas become
 *   `board_buff` (permanent) / temp `board_buff` (combatBuff deltas).
 *   Negative temp deltas are suppressed — that's a buff expiring at
 *   round commit, not a moment worth flashing.
 * - Shop cards are matched by instanceId across the arrays (a buy
 *   removes one), stat deltas become `shop_buff`.
 * - A health increase becomes `hero_heal`. Decreases are ignored here —
 *   combat damage has its own presentation.
 *
 * Gold changes are NOT diffed (every action has an intrinsic cost);
 * call sites emit explicit `gold_gain` events where a power grants gold.
 */
export function diffTavernFx(
  prev: TavernFxSnapshot,
  next: TavernFxSnapshot,
): TavernFxEvent[] {
  const events: TavernFxEvent[] = [];

  for (let slot = 0; slot < next.board.length; slot++) {
    const before = prev.board[slot];
    const after = next.board[slot];
    if (!before || !after) continue;
    if (before.instanceId !== after.instanceId) continue;

    const atk = after.attack - before.attack;
    const hp = after.health - before.health;
    if (atk !== 0 || hp !== 0) {
      events.push({ id: nextFxId(), kind: 'board_buff', slot, atk, hp, temp: false });
    }

    const tempAtk =
      (after.combatBuff?.attack ?? 0) - (before.combatBuff?.attack ?? 0);
    const tempHp =
      (after.combatBuff?.health ?? 0) - (before.combatBuff?.health ?? 0);
    if ((tempAtk > 0 || tempHp > 0) && tempAtk >= 0 && tempHp >= 0) {
      events.push({
        id: nextFxId(),
        kind: 'board_buff',
        slot,
        atk: tempAtk,
        hp: tempHp,
        temp: true,
      });
    }
  }

  for (const after of next.shop) {
    if (!after.instanceId) continue;
    const before = prev.shop.find((c) => c.instanceId === after.instanceId);
    if (!before) continue;
    const atk = after.attack - before.attack;
    const hp = after.health - before.health;
    if (atk !== 0 || hp !== 0) {
      events.push({
        id: nextFxId(),
        kind: 'shop_buff',
        instanceId: after.instanceId,
        atk,
        hp,
      });
    }
  }

  if (next.health > prev.health) {
    events.push({ id: nextFxId(), kind: 'hero_heal', amount: next.health - prev.health });
  }

  return events;
}

/** Explicit gold-gain event for call sites where a power grants gold. */
export function goldGainFx(amount: number): TavernFxEvent {
  return { id: nextFxId(), kind: 'gold_gain', amount };
}
